import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./index.css";

import { Menu } from "../../components/Menu";
import { PDVItem } from "../../components/PDVItem";
import { Loading } from "../../components/Loading";

import { useAppConfig } from "../../hooks/useAppConfig";
import { ConfigType } from "../../contexts/AppConfigContext";
import api from "../../services/api";
import { usePluginspace } from "../../hooks/usePluginspace";
import { ProductType } from "../../contexts/ProductsContext";
import useAuth from "../../hooks/useAuth";
import ReactLoading from "react-loading";

export function Favorites() {
  const navigate = useNavigate();
  const { getAppConfig } = useAppConfig();
  const { getToken, registerAccess } = usePluginspace();
  const { loginInfo } = useAuth();

  const [config, setConfig] = useState<ConfigType | void>();
  const [products, setProducts] = useState<Array<ProductType>>([]);
  const [loading, setLoading] = useState(false);
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [primaryColor, setPrimaryColor] = useState("#078BD8");

  // Estados de Paginação
  const [page, setPage] = useState(0);
  const [request, setRequest] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  useEffect(() => {
    registerAccess("favorites");
    if (loginInfo && loginInfo.id) {
      loadFavorites(0); // Inicia na página 0
    } else {
      loadOnlyConfig();
    }
  }, [loginInfo.id]);

  async function loadOnlyConfig() {
    setLoading(true);
    setLoadingHeader(true);
    try {
      const conf = await getAppConfig();
      setConfig(conf);
      if (conf?.webappcolor?.webappbutton) {
        setPrimaryColor(conf.webappcolor.webappbutton);
      }
    } catch (error) {
      console.log(error);
    }
    setLoadingHeader(false);
    setLoading(false);
  }

  async function loadFavorites(pageToLoad: number, append = false) {
    if (pageToLoad === 0) setLoading(true);
    setLoadingHeader(true);
    try {
      const conf = await getAppConfig();
      setConfig(conf);
      if (conf?.webappcolor?.webappbutton) {
        setPrimaryColor(conf.webappcolor.webappbutton);
      }
      setLoadingHeader(false);

      // Usando a rota de post com body pageOptionsDto para paginar
      await api
        .post(
          `/api/product-favorite/customer/products/${loginInfo.id}`,
          {
            take: 10,
            skip: pageToLoad * 10,
            page: pageToLoad,
            order: "DESC",
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          },
        )
        .then(async (response) => {
          // O backend retorna um PageDto (entities, meta)
          const data = response.data.data;

          const newProducts = data.map((favorite: any) => {
            const prod = favorite.product;
            const variations = favorite.variation ||
              prod.variations || [
                { price: 0, promotionalPrice: 0, img: "", referenceCode: "" },
              ];
            const images = prod.images || [];

            let img = "";
            const firstVariation = variations[0];
            if (firstVariation && firstVariation.img) {
              img = firstVariation.img.includes("http")
                ? firstVariation.img
                : `https://s3.amazonaws.com/img.ninegrid.com/${firstVariation.img}`;
            }

            const processedImages = images.map((image: any) => {
              if (image.imgsuffix && !image.imgsuffix.includes("http")) {
                image.imgsuffix = `${image.path}/${image.imgsuffix}`;
              }
              return image;
            });

            return {
              id: prod.id,
              classificationId: prod.classificationId,
              code: prod.code,
              description: prod.description,
              name: prod.name,
              status: prod.status,
              images: processedImages,
              img: img,
              technicalSpecification: prod.technicalSpecification,
              variations: variations,
              order: prod.order || 0,
            } as ProductType;
          });

          setProducts((prev) =>
            append ? [...prev, ...newProducts] : newProducts,
          );
          setHasNextPage(newProducts.length >= 10);
          setPage(pageToLoad);
          setRequest(false);
        });
    } catch (error) {
      console.log(error);
      setRequest(false);
    }
    setLoading(false);
  }

  function handleScroll(event: any) {
    const bottom =
      event.target.scrollHeight -
        event.target.scrollTop -
        event.target.clientHeight <=
      50;
    if (bottom && !request && hasNextPage) {
      setRequest(true);
      loadFavorites(page + 1, true);
    }
  }

  return (
    <div className="perfil-favorites-wrapper">
      <div className="content-perfil-favorites">
        {loading && page === 0 ? (
          <div className="loading-container">
            <Loading
              type="spinningBubbles"
              color="#FFF"
              width={64}
              height={64}
            />
          </div>
        ) : (
          <div
            className="perfil-favorites-outlet-container"
            onScroll={handleScroll}
          >
            <button
              className="btn-back-perfil-favorites"
              onClick={() => navigate(-1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Voltar
            </button>

            <div className="perfil-favorites-header">
              <h2>Produtos Favoritos</h2>
            </div>

            {products.length === 0 ? (
              <div className="perfil-favorites-empty">
                <p>Você ainda não possui produtos favoritos.</p>
              </div>
            ) : (
              <div className="perfil-favorites-list">
                {products.map((product) => {
                  const productImg =
                    product.images?.find(
                      (image) =>
                        image.typeimg === "image" ||
                        image.typeimg === "mainpicture",
                    )?.imgsuffix || "/imagem/produto-sem-imagem.png";

                  return (
                    <PDVItem
                      id={product.id}
                      name={product.name}
                      reference_id={product.variations[0]?.referenceCode || ""}
                      price={product.variations[0]?.price || 0}
                      promotionalPrice={
                        product.variations[0]?.promotionalPrice || 0
                      }
                      image={productImg}
                      inBlocks={false}
                      key={product.id}
                      description={product.description}
                      food={
                        config?.template === "food" ||
                        config?.template === "retail"
                      }
                      fontColor={"#636363"}
                      discountCoupon={{
                        color: config?.webappcolor.flagDiscountColor || "#000",
                        type: config?.content.flagDiscount || "trigonal",
                      }}
                      stock={!!product.variations.find((v) => v.inventory > 0)}
                      mark={product.mark?.name || "-/-"}
                      noImage={config?.content.pdv.noProductImages || false}
                      isVertical={config?.content.typeImage !== "square"}
                    />
                  );
                })}
                {request && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "10px",
                    }}
                  >
                    <ReactLoading type="bubbles" color="#b5b5b5" height={30} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!loadingHeader && (
          <Menu
            fontColor={config?.webappcolor.iconsfooter || "#636363"}
            activeFontColor={config?.webappcolor.highlightfooter || "#000"}
            backgroundMenu={config?.webappcolor.backgroundfooter || "#F5F5F5"}
            colorIcons={config?.webappcolor.iconsfooter || "#636363"}
            highlightColorIcons={
              config?.webappcolor.highlightfooter || "#636363"
            }
            backgroundCart={config?.webappcolor.backgroundcart || "#FFBA49"}
            fontCart={config?.webappcolor.fontcart || "#000"}
            noSales={config?.content.pdv.noSales || false}
          />
        )}
      </div>
    </div>
  );
}
